<template>
  <!--
    อยู่ใน flow ของ layout ไม่ใช่ fixed/absolute — ดันทั้งหน้าลงมา
    ถ้าลอยทับ ปุ่มโต๊ะ/ปุ่มคิดเงินแถวบนจะกดไม่โดน ซึ่งทำร้านพังกลางวันได้เลย
  -->
  <div
    v-if="message"
    class="flex w-full items-center gap-3 px-4 py-2"
    :class="LEVEL_CLASS[level]"
    role="status"
    :aria-label="$t('app_control.label.announcement')"
  >
    <!-- ตัวใหญ่ไว้ก่อน จอ POS อยู่ห่างจากคนอ่านเป็นเมตร ตัวเล็กคืออ่านไม่ออก = ไม่มีประโยชน์ -->
    <p class="flex-1 text-xl font-semibold leading-snug lg:text-2xl">
      {{ message }}
    </p>

    <button
      v-if="announcement?.dismissible"
      type="button"
      class="shrink-0 rounded px-3 py-1 text-2xl leading-none opacity-80 hover:opacity-100"
      :aria-label="$t('app_control.btn.close')"
      @click="store.dismissAnnouncement()"
    >
      &times;
    </button>
  </div>
</template>

<script lang="ts" setup>
import type { AnnouncementLevel } from "~/interface/AppControl";

/**
 * ประกาศจาก Firestore `app_control/{channel}.announcement`
 *
 * ข้อความมาจาก Firestore ไม่ใช่ไฟล์แปล (i18n) เพราะทีมซัพพอร์ตต้องพิมพ์เองได้
 * ตอนระบบมีปัญหา รอ deploy ไม่ได้ — เลยเก็บทั้งสองภาษาไว้ใน doc แล้วเลือกตาม locale
 */

// นาฬิกาเดินช้า ๆ ไว้เช็ค startAt/endAt เท่านั้น ไม่ต้องละเอียดกว่านี้
const CLOCK_TICK_MS = 30_000;

const LEVEL_CLASS: Record<AnnouncementLevel, string> = {
  info: "bg-blue-600 text-white",
  warning: "bg-yellow-300 text-yellow-950",
  error: "bg-red-600 text-white",
};

const store = useAppControlStore();
const { locale } = useI18n();

const now = ref(Date.now());
const clock = setInterval(() => {
  now.value = Date.now();
}, CLOCK_TICK_MS);

onBeforeUnmount(() => clearInterval(clock));

const announcement = computed(() => store.announcement);

/** level ที่พิมพ์มาผิดให้ตกเป็น info ดีกว่าไม่โชว์ประกาศเลย */
const level = computed<AnnouncementLevel>(() => {
  const value = announcement.value?.level;
  return value && value in LEVEL_CLASS ? value : "info";
});

const inTimeWindow = computed(() => {
  const value = announcement.value;
  if (!value) return false;

  const startAt = parseIso(value.startAt);
  if (startAt !== null && now.value < startAt) return false;

  const endAt = parseIso(value.endAt);
  if (endAt !== null && now.value > endAt) return false;

  return true;
});

// กดปิดแล้วต้องเงียบจนกว่าจะมีประกาศ "ตัวใหม่" ซึ่งวัดจาก updatedAt
// ถ้าวัดจากตัวข้อความ การแก้คำผิดนิดเดียวจะเด้งกลับมากวนทุกเครื่อง
const dismissed = computed(() => {
  const value = announcement.value;
  if (!value?.dismissible) return false;

  const marker = value.updatedAt || "no-updated-at";
  return store.announcementDismissedAt === marker;
});

const message = computed(() => {
  if (!announcement.value) return "";
  if (!inTimeWindow.value) return "";
  if (dismissed.value) return "";

  const { message_th, message_en } = announcement.value;

  // ภาษาที่ผู้ใช้เลือกไม่มีข้อความ ก็เอาอีกภาษาไปก่อน ดีกว่าแถบว่างเปล่า
  return (
    (locale.value === "th" ? message_th || message_en : message_en || message_th) || ""
  );
});

function parseIso(value: string | undefined): number | null {
  if (!value) return null;

  const time = Date.parse(value);
  // พิมพ์วันที่ผิด = เมินเงื่อนไขข้อนั้นไป ไม่ใช่ซ่อนประกาศทิ้งทั้งอัน
  return Number.isNaN(time) ? null : time;
}
</script>
